import apiClient from "./client";
import type {
  FriendRequestItem,
  FriendshipItem,
  FriendsOverview,
  PublicProfile,
  SocialInbox,
  SpeakingInviteItem,
} from "@/types";

export async function searchSocialProfiles(q: string): Promise<PublicProfile[]> {
  const { data } = await apiClient.get<PublicProfile[]>("/social/search", {
    params: { q },
  });
  return data;
}

export async function getPublicProfile(username: string): Promise<PublicProfile> {
  const { data } = await apiClient.get<PublicProfile>(
    `/social/profiles/${encodeURIComponent(username)}`
  );
  return data;
}

export async function getFriendsOverview(): Promise<FriendsOverview> {
  const { data } = await apiClient.get<FriendsOverview>(
    "/social/friends/overview"
  );
  return data;
}

export async function sendFriendRequest(
  username: string
): Promise<FriendRequestItem> {
  const { data } = await apiClient.post<FriendRequestItem>(
    "/social/friends/requests",
    { username }
  );
  return data;
}

export async function acceptFriendRequest(
  requestId: number
): Promise<FriendshipItem> {
  const { data } = await apiClient.post<FriendshipItem>(
    `/social/friends/requests/${requestId}/accept`
  );
  return data;
}

export async function declineFriendRequest(
  requestId: number
): Promise<FriendRequestItem> {
  const { data } = await apiClient.post<FriendRequestItem>(
    `/social/friends/requests/${requestId}/decline`
  );
  return data;
}

export async function unfriendUser(userId: number): Promise<void> {
  await apiClient.delete(`/social/friends/${userId}`);
}

export async function sendSpeakingInvite(
  username: string,
  roomCode: string
): Promise<SpeakingInviteItem> {
  const { data } = await apiClient.post<SpeakingInviteItem>(
    "/social/speaking-invites",
    { username, room_code: roomCode }
  );
  return data;
}

export async function acceptSpeakingInvite(
  inviteId: number
): Promise<SpeakingInviteItem> {
  const { data } = await apiClient.post<SpeakingInviteItem>(
    `/social/speaking-invites/${inviteId}/accept`
  );
  return data;
}

export async function declineSpeakingInvite(
  inviteId: number
): Promise<SpeakingInviteItem> {
  const { data } = await apiClient.post<SpeakingInviteItem>(
    `/social/speaking-invites/${inviteId}/decline`
  );
  return data;
}

export async function getSocialInbox(): Promise<SocialInbox> {
  const { data } = await apiClient.get<SocialInbox>("/social/inbox");
  return data;
}

export async function markNotificationRead(
  notificationId: number
): Promise<void> {
  await apiClient.post(`/social/notifications/${notificationId}/read`);
}

export async function markAllNotificationsRead(): Promise<void> {
  await apiClient.post("/social/notifications/read-all");
}

export async function getVapidPublicKey(): Promise<string | null> {
  const { data } = await apiClient.get<{ public_key: string | null }>(
    "/social/push/vapid-public-key"
  );
  return data.public_key;
}

export async function subscribePush(subscription: {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}): Promise<void> {
  await apiClient.post("/social/push/subscribe", subscription);
}

export async function unsubscribePush(endpoint: string): Promise<void> {
  await apiClient.post("/social/push/unsubscribe", { endpoint });
}
